/**
 * EVERY poll the page makes goes through here (docs/notes/tmux-view.md), so each
 * behaves the same on a slow server:
 *
 * - nothing is asked while the tab is hidden; one that fell due meanwhile runs
 *   as soon as the tab is shown again;
 * - a call never starts while the last is still out, and the next is timed
 *   from when the reply landed (`setTimeout` chained, never `setInterval`), so a
 *   slow server is not sent a queue of them;
 * - a call that took over `SLOW_MS`, threw or returned `false` doubles the wait,
 *   up to `MAX_FACTOR` times the base; a quick success puts it back;
 * - `active` gates it further (a window put away, a view not on screen): checked
 *   at each tick, and a tick it skipped runs as soon as `now()` is called.
 *
 * Prefer a server push to a new poll; a poll is for what nothing announces.
 */
import { onBeforeUnmount, onMounted } from "vue";

const SLOW_MS = 1000;
const MAX_FACTOR = 4;

export interface PollOptions {
  /** Only polls while this says so; a tick it skips waits for the next. */
  active?: () => boolean;
  /** Call once as it starts (default), or wait a whole interval first. */
  immediate?: boolean;
}

export interface Poll {
  /** Call it now — after the call that is out, if one is — whatever the gates say. */
  now(): Promise<void>;
  stop(): void;
}

/** `fn` every `baseMs` (more while slow or failing) until `stop()`. For modules;
 *  a component uses `usePoll`. */
export function startPoll(fn: () => unknown, baseMs: number, opts: PollOptions = {}): Poll {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let out: Promise<void> | null = null;
  /** A `now()` asked for while a call was out: run once more after it. */
  let again: Promise<void> | null = null;
  let factor = 1;
  let stopped = false;
  /** A tick fell while the tab was hidden. */
  let due = false;

  const schedule = (): void => {
    if (timer) clearTimeout(timer);
    timer = stopped ? null : setTimeout(tick, baseMs * factor);
  };

  function run(): Promise<void> {
    if (timer) clearTimeout(timer);
    timer = null;
    due = false;
    const started = Date.now();
    out = (async () => {
      let ok = true;
      try {
        ok = (await fn()) !== false;
      } catch {
        ok = false;
      }
      factor = ok && Date.now() - started <= SLOW_MS ? 1 : Math.min(MAX_FACTOR, factor * 2);
      out = null;
      if (!again) schedule();
    })();
    return out;
  }

  function tick(): void {
    timer = null;
    if (stopped) return;
    if (document.hidden) {
      // Picked up by the visibility listener rather than polling a hidden tab.
      due = true;
      return;
    }
    if (opts.active && !opts.active()) {
      schedule();
      return;
    }
    void run();
  }

  function now(): Promise<void> {
    if (stopped) return Promise.resolve();
    if (!out) return run();
    again ??= out.then(() => {
      again = null;
      return stopped ? undefined : run();
    });
    return again;
  }

  const onVisible = (): void => {
    if (!document.hidden && due && !stopped && !out) tick();
  };
  document.addEventListener("visibilitychange", onVisible);

  if (opts.immediate === false) schedule();
  else tick();

  return {
    now,
    stop(): void {
      stopped = true;
      if (timer) clearTimeout(timer);
      timer = null;
      document.removeEventListener("visibilitychange", onVisible);
    },
  };
}

/** `startPoll` for a component: started on mount, stopped on unmount. `now()`
 *  before mount does nothing. */
export function usePoll(fn: () => unknown, baseMs: number, opts: PollOptions = {}): Poll {
  let poll: Poll | null = null;
  onMounted(() => {
    poll = startPoll(fn, baseMs, opts);
  });
  onBeforeUnmount(() => {
    poll?.stop();
    poll = null;
  });
  return {
    now: () => poll?.now() ?? Promise.resolve(),
    stop: () => {
      poll?.stop();
      poll = null;
    },
  };
}
